import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MousePointerClick, TrendingUp, ShoppingBag, Tag, Store, Banknote, Loader2,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Header } from "../components/Header";
import { AdminNav } from "../components/AdminNav";
import { fetchAffiliateStats } from "../lib/api";
import { AFFILIATE } from "../constants/testIds";

const StatCard = ({ icon: Icon, label, value, n }) => (
  <div data-testid={AFFILIATE.statCard(n)} className="bg-card border border-border rounded-2xl p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</span>
      <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
        <Icon className="w-4 h-4 text-emerald-500" />
      </div>
    </div>
    <div className="font-display font-bold text-3xl tracking-tight">{value}</div>
  </div>
);

const List = ({ icon: Icon, title, items, valueKey = "count" }) => (
  <div className="bg-card border border-border rounded-2xl p-5">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-emerald-500" />
      <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{title}</span>
    </div>
    <div className="divide-y divide-border">
      {items.length === 0 && <div className="text-sm text-muted-foreground py-3">No data yet.</div>}
      {items.map((p, i) => (
        <div key={i} className="flex items-center justify-between py-2.5">
          <span className="text-sm truncate pr-3">{p.name}</span>
          <span className="text-xs font-semibold text-emerald-500">{p[valueKey]}</span>
        </div>
      ))}
    </div>
  </div>
);

export default function AdminAffiliate() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!localStorage.getItem("sbi_admin_token")) {
      navigate("/admin");
      return;
    }
    fetchAffiliateStats()
      .then(setData)
      .catch((e) => {
        if (e?.response?.status === 401) {
          localStorage.removeItem("sbi_admin_token");
          navigate("/admin");
        } else setErr(e?.message || "Failed to load");
      });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("sbi_admin_token");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-background" data-testid={AFFILIATE.page}>
      <Header />
      <main className="max-w-7xl mx-auto px-5 py-8">
        <AdminNav onLogout={logout} />

        <div className="mb-5">
          <h1 className="font-display font-bold text-2xl tracking-tight">Affiliate Analytics</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Click-through performance across Amazon Associates, Awin and other future networks.
          </p>
        </div>

        {err && <div className="text-sm text-red-400">{err}</div>}
        {!data && !err && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-emerald-500" /> Loading…
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard icon={MousePointerClick} label="Total clicks" value={data.total_clicks} n="clicks" />
              <StatCard icon={TrendingUp} label="CTR" value={`${data.ctr}%`} n="ctr" />
              <StatCard icon={Tag} label="Searches" value={data.total_searches} n="searches" />
              <StatCard icon={Banknote} label="Est. revenue" value={`£${data.estimated_revenue_gbp || 0}`} n="revenue" />
            </div>

            <div className="grid lg:grid-cols-3 gap-4 mb-6">
              <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Clicks · last 7 days</span>
                </div>
                <div className="h-56">
                  {data.growth.length === 0 ? (
                    <div className="text-sm text-muted-foreground h-full flex items-center justify-center">No data yet.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.growth}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                        <Line type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2.5} dot={{ fill: "#10B981" }} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <List icon={ShoppingBag} title="By CTA" items={data.by_cta} />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <List icon={Tag} title="Top clicked products" items={data.top_products} />
              <List icon={Store} title="Top clicked categories" items={data.top_categories} />
              <List icon={ShoppingBag} title="Top retailers" items={data.top_retailers} />
            </div>
          </>
        )}
      </main>
    </div>
  );
}
